import React, { useEffect, useRef, useState } from 'react';
import { Link } from 'react-router-dom';
import { Check, MapPin, Pencil } from 'lucide-react';
import type { FacilityConfig } from '../../services/missionControl.js';
import {
  getCanopusFacilities,
  getDemoFacilityName,
  setDemoFacilityName,
  isDemoFacility,
  DEFAULT_DEMO_FACILITY_NAME,
} from './data/demo-facility.js';
import './facility-select.css';

export function CanopusFacilitySelectPage(): React.ReactElement {
  const [facilities, setFacilities] = useState<FacilityConfig[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  // Browser-local demo facility name (localStorage). Keeping it client-side
  // means concurrent demos on the same deployment don't clobber each other.
  const [demoName, setDemoName] = useState<string>(() => getDemoFacilityName());
  const [renaming, setRenaming] = useState(false);
  const renameRef = useRef<HTMLFormElement | null>(null);
  const renameInputRef = useRef<HTMLInputElement | null>(null);

  useEffect(() => {
    let cancelled = false;
    getCanopusFacilities()
      .then((data) => {
        if (!cancelled) setFacilities(data);
      })
      .catch((err) => {
        if (!cancelled) setError(err instanceof Error ? err.message : 'Failed to load facilities');
      })
      .finally(() => {
        if (!cancelled) setLoading(false);
      });
    return () => {
      cancelled = true;
    };
  }, []);

  useEffect(() => {
    if (!renaming) return;
    renameInputRef.current?.focus();
    renameInputRef.current?.select();
  }, [renaming]);

  // Close inline rename on outside click.
  useEffect(() => {
    if (!renaming) return;
    const onDocMouseDown = (e: MouseEvent) => {
      if (renameRef.current && !renameRef.current.contains(e.target as Node)) {
        setRenaming(false);
      }
    };
    document.addEventListener('mousedown', onDocMouseDown);
    return () => document.removeEventListener('mousedown', onDocMouseDown);
  }, [renaming]);

  const handleDemoNameChange = (value: string) => {
    setDemoName(value);
    setDemoFacilityName(value);
  };

  const finishRename = () => setRenaming(false);

  const effectiveDemoName = demoName.trim() || DEFAULT_DEMO_FACILITY_NAME;

  if (error) {
    return (
      <div className="facility-select">
        <p className="facility-select__error">{error}</p>
      </div>
    );
  }

  return (
    <div className="facility-select">
      <div className="facility-select__header">
        <h1 className="facility-select__title">Canopus Command — Select Facility</h1>
        <p className="facility-select__subtitle">
          Choose a commercial facility to open its Canopus dashboard
        </p>
      </div>

      {loading ? (
        <div className="facility-select__grid">
          {Array.from({ length: 4 }).map((_, i) => (
            <div key={i} className="facility-select__card facility-select__card--skeleton">
              <div className="facility-select__card-info">
                <p className="facility-select__card-name loading-blur">Facility Name</p>
                <p className="facility-select__card-region loading-blur">City, State</p>
              </div>
            </div>
          ))}
        </div>
      ) : facilities.length === 0 ? (
        <p className="facility-select__empty">No commercial facilities found.</p>
      ) : (
        <div className="facility-select__grid">
          {facilities.map((f) => {
            const isDemo = isDemoFacility(f);
            const displayName = isDemo ? effectiveDemoName : f.facilityName;
            // Seed the router state with the live demo name so the dashboard
            // header shows the edited name without waiting on a re-fetch.
            const linkFacility = isDemo ? { ...f, facilityName: displayName } : f;
            return (
              <Link
                key={f.id}
                to={`/canopus-command/${f.id}`}
                state={{ facility: linkFacility }}
                className={`facility-select__card${isDemo ? ' facility-select__card--demo' : ''}`}
                onClick={(e) => {
                  // Don't navigate while the demo name is being edited.
                  if (isDemo && renaming) {
                    e.preventDefault();
                  }
                }}
              >
                <div className="facility-select__card-info">
                  {isDemo && <span className="facility-select__badge">Demo</span>}

                  {isDemo && renaming ? (
                    <form
                      className="facility-select__rename"
                      ref={renameRef}
                      autoComplete="off"
                      onSubmit={(e) => {
                        e.preventDefault();
                        finishRename();
                      }}
                      onClick={(e) => {
                        e.preventDefault();
                        e.stopPropagation();
                      }}
                    >
                      {/* Chrome ignores autocomplete=off on fields that look like a
                          person/org name. A readonly field + new-password token is
                          the reliable way to suppress the autofill dropdown. */}
                      <input
                        type="text"
                        tabIndex={-1}
                        aria-hidden="true"
                        autoComplete="username"
                        value=""
                        readOnly
                        className="facility-select__sr-only"
                      />
                      <label className="facility-select__sr-only" htmlFor="cc-demo-facility-label">
                        Demo facility title
                      </label>
                      <input
                        id="cc-demo-facility-label"
                        ref={renameInputRef}
                        className="facility-select__rename-input"
                        type="text"
                        autoComplete="new-password"
                        autoCorrect="off"
                        autoCapitalize="off"
                        spellCheck={false}
                        data-1p-ignore="true"
                        data-lpignore="true"
                        data-bwignore="true"
                        data-form-type="other"
                        readOnly
                        value={demoName}
                        placeholder={DEFAULT_DEMO_FACILITY_NAME}
                        aria-label="Demo facility title"
                        onFocus={(e) => {
                          e.currentTarget.removeAttribute('readonly');
                        }}
                        onChange={(e) => handleDemoNameChange(e.target.value)}
                        onKeyDown={(e) => {
                          if (e.key === 'Enter' || e.key === 'Escape') {
                            e.preventDefault();
                            e.stopPropagation();
                            finishRename();
                          }
                        }}
                      />
                      <p className="facility-select__rename-hint">Saved in this browser only</p>
                    </form>
                  ) : (
                    <p className="facility-select__card-name">{displayName}</p>
                  )}

                  <p className="facility-select__card-region">
                    <MapPin size={14} />
                    {f.location ?? f.region}
                  </p>
                </div>

                {isDemo && (
                  <button
                    type="button"
                    className={`facility-select__rename-btn${renaming ? ' facility-select__rename-btn--active' : ''}`}
                    aria-label={renaming ? 'Done renaming' : 'Rename demo facility'}
                    aria-pressed={renaming}
                    onClick={(e) => {
                      e.preventDefault();
                      e.stopPropagation();
                      if (renaming) finishRename();
                      else setRenaming(true);
                    }}
                  >
                    {renaming ? <Check size={16} /> : <Pencil size={16} />}
                  </button>
                )}
              </Link>
            );
          })}
        </div>
      )}
    </div>
  );
}
